<template>
<td>
    <input 
        v-if="!retired" 
        @change="selectOption();" 
        v-model="inputValue" 
        type="number" 
        :min="option.min_qty" 
        :max="option.max_qty" 
        :step="option.qty_increment" 
        :name="'config-'+option.groupid" 
        size="5" />
    <span class="text-primary" style="margin-left: 10px;">
        <b>{{option.config_id}}</b> | {{option.description}}
    </span>
    <span v-if="orderMeta.level <= 3"
        class="hidden-xs">
        <span class="part"> -
            {{option.part_id}}</span>
    </span>
    <div class="idetail" v-show="!hideDetails || expanded">
        <p 
            v-if="option.user_input" 
            class="alert icon" 
            :class="{'alert-info': valid && !wasInvalid,'alert-danger': !valid, 'alert-success': valid && wasInvalid}">
            Allowed values: {{option.min_qty}}-{{option.max_qty}} {{option.uom}}, increments of {{option.qty_increment}}{{option.uom}}
        </p>
        <div v-html="option.option_exp"></div>
    </div>
</td>
</template>

<script>
import useProductConfig from '../composables/useProductConfig';
import useProductInfo from '../composables/useProductInfo';
import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api';

export default {
    name: 'optionInput',
    props: {
        option: {
            optid: Number,
            product_id: Number,
            inputValue: String,
            min_qty: Number,
            max_qty: Number,
            qty_increment: Number,
            groupid: Number,
            user_input: Boolean,
            config_id: String,
            uom: String,
            option_exp: String,
            description: String
        },
        hideDetails: Boolean,
        retired: Number,
        expandedGroups: Array
    },
    setup: function (props,{emit}) {
        const {
            isInMissingInput,
            cartInError,
            removeOption,
            addRequiredInput,
            removeRequiredInput,
            getOptionInConfig,
            optionInConfig
        } = useProductConfig();
        const { orderMeta } = useProductInfo();

        const inputValue = ref('');
        const config_id = ref('');
        const wasInvalid = ref(false);
        const dirty = ref(false);

        const selectOption = () => {
            dirty.value = true;
            if (!props.option.user_input || isValidInputValue.value) {
                props.option.inputValue = inputValue.value;
                emit('config-updated', props.option);
            }
        }

        const isValidInputValue = computed(() => {
            if (
                inputValue.value < props.option.min_qty ||
                inputValue.value > props.option.max_qty ||
                inputValue.value % props.option.qty_increment != 0 || 
                inputValue.value == ''
            ) {
                removeOption(props.option); //Remove the option from the config so that it can't be added to the cart and it doesn't show a bad configname 
                wasInvalid.value = true; //So the alert box is green once the input is valid again
                emit('input-error'); //hideDetails is a prop controller by the parent, so tell the parent we have an error
                return false;
            }
            return true;
        });

        const valid = computed(() => {
            if (isCartInError.value && isInMissingInput(props.option)) {
                emit('input-error');
                return false;
            }
            return (isCartInError.value || dirty.value) ? isValidInputValue.value : true;
        });

        const expanded = computed(() => {
            var groupid = props.option.groupid;
            var index = props.expandedGroups.findIndex(function (item) {
                return item.groupid == groupid;
            });
            return (index > -1) ? props.expandedGroups[index].state : false;
        });

        const isCartInError = computed(() => {
            return cartInError(props.option.product_id);
        });

        watch(valid,(newVal) => {
            if (newVal == false) {
                wasInvalid.value = true;
            }
        });

        onMounted(() => {
            //  Because we are using v-if to show/hide this component, mounted is called each time the component is displayed to the user,
            //  which is nice because we can check for default options (first in group) if needed. Had we use v-show we'd save some DOM 
            //  manipulation for the brwoser but we'd have some overhead in keeping track of the state changes I think.
            if (props.option.user_input) {
                addRequiredInput(props.option);
                //For preconfigured, the preset input value is in the config. Retreive it and set the component data property
                if(optionInConfig(props.option.optid,props.option.product_id)){
                    var option = getOptionInConfig(props.option.optid,props.option.product_id)[0];
                    inputValue.value = option.inputValue;
                }
            }
        });

        onBeforeUnmount(() => {
            removeRequiredInput(props.option);
        });

        return {
            valid,
            wasInvalid,
            inputValue,
            config_id,
            selectOption,
            expanded,
            orderMeta
        }
    },
    emits: ['config-updated', 'input-error']
}
</script>
